/**
 * Facebook Pixel Adapter for Analytics System
 *
 * Uses the Facebook Pixel for more advanced tracking of events than just the conversions API
 * If adblockers are not in place, this is the preffered
 *
 */

class FacebookPixelAdapter {
  /**
   * Validate event name against Facebook Pixel standards
   * @param {string} eventName - Event name to validate
   * @returns {string} - Validated event name
   */
  static validateEventName(eventName) {
    // Default to 'CustomEvent' if no event name provided
    if (!eventName || typeof eventName !== 'string') {
      return 'CustomEvent';
    }

    // List of standard Facebook events
    const standardEvents = [
      'PageView',
      'Purchase',
      'AddToCart',
      'ViewContent',
      'CompleteRegistration',
      'Lead',
      'Search',
      'AddToWishlist',
      'InitiateCheckout',
      'AddPaymentInfo',
      'Contact',
      'Subscribe',
    ];

    // Get the event parameter and validate it
    const requestedEvent = eventName.trim();

    // Check if it's a standard event (case-sensitive match)
    if (standardEvents.includes(requestedEvent)) {
      return requestedEvent;
    }

    // For custom events, validate the string (alphanumeric with underscores)
    // Facebook allows custom event names with letters, numbers, and underscores
    if (/^[a-zA-Z0-9_]+$/.test(requestedEvent) && requestedEvent.length <= 50) {
      return requestedEvent;
    }

    // If validation fails, return default 'CustomEvent'
    return 'CustomEvent';
  }

  /**
   * Track an event to Facebook Pixel
   * @param {string} eventName - Event title/name
   * @param {Object} eventProperties - Event data
   */
  static track(eventName, eventProperties = {}) {
    try {
      // Validate and sanitize event name
      const validatedEventName = this.validateEventName(eventName);

      // Prepare event data with Facebook Pixel format
      const pixelEventData = {
        content_name: validatedEventName,
        source_url: eventProperties.page_location || window.location.href,
        timestamp: Date.now(),
        ...eventProperties,
      };

      // Track the event
      window.fbq('track', validatedEventName, pixelEventData);

      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log(`Facebook Pixel Event: ${validatedEventName}`, pixelEventData);
      }
    } catch (error) {
      // Fail silently - don't break user experience
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('Facebook Pixel tracking error:', error);
      }
    }
  }

  /**
   * Identify a user (Facebook Pixel doesn't have traditional identify,
   * but we can use this for advanced matching if needed)
   * @param {Object} userProperties - User data
   */
  static identify(userProperties = {}) {
    try {
      if (userProperties.email || userProperties.user_id) {
        // Use fbq init function for user identification with advanced matching
        // the facebook pixel handles hashing of the email
        // the external_id is the user_id from the database
        window.fbq('init', '1122040979428709', {
          em: userProperties.email ? userProperties.email.toLowerCase().trim() : undefined,
          fn: userProperties.firstName ? userProperties.firstName : undefined,
          ln: userProperties.lastName ? userProperties.lastName : undefined,
          external_id: userProperties.user_id,
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('Facebook Pixel identify error:', error);
      }
    }
  }
}

export default FacebookPixelAdapter;
